/**
 * @file EveningMeditationStep.tsx
 *
 * @description Step component for evening meditation in the Miracle Flow.
 * Provides a timer-based meditation session for evening reflection and rest preparation.
 *
 * @module miracle-flow
 *
 * @domain productivity
 *
 */

import { Brain } from "lucide-react";
import React from "react";

import { Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { Timer } from "../../atoms/Timer";

/**
 * Props for EveningMeditationStep component
 */
export interface EveningMeditationStepProperties {
  className?: string;
  duration: number;
  isActive: boolean;
  onComplete: () => void;
  onStart: () => void;
}

/**
 * EveningMeditationStep - Evening meditation timer step
 *
 * Features:
 * - Timer-based meditation session
 * - Progress visualization
 * - Start/complete callbacks
 * - Evening-focused messaging
 *
 * @param root0
 *
 * @param root0.duration
 *
 * @param root0.isActive
 *
 * @param root0.onStart
 *
 * @param root0.onComplete
 *
 * @param root0.className
 *
 * @example
 * ```tsx
 * <EveningMeditationStep
 *   duration={600}
 *   isActive={isMeditating}
 *   onStart={startMeditation}
 *   onComplete={completeMeditation}
 * />
 * ```
 *
 * @returns JSX.Element
 *
 */
export const EveningMeditationStep: React.FC<EveningMeditationStepProperties> = ({
  className,
  duration,
  isActive,
  onComplete,
  onStart,
}) => {
  return (
    <div className={cn("space-y-6 w-full", className)}>
      {/* Hero Section */}
      <Stack align="center" className="py-6 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-tracq-deep-orange to-tracq-light-orange shadow-tracq-card">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-tracq-light-orange" level="h3" size="xl">
            Evening Meditation
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Take a moment to find peace and prepare for rest. Focus on your breathing and let go of
            the day.
          </p>
        </Stack>
      </Stack>

      {/* Timer Section */}
      <Stack align="center" className="py-8 w-full" direction="col" gap={4}>
        <Timer
          duration={duration}
          isActive={isActive}
          onComplete={onComplete}
          onStart={onStart}
          showProgress
          size="lg"
        />
        {!isActive && (
          <p className="text-[#94A3B8] text-sm text-center">
            Click the timer to begin your evening meditation
          </p>
        )}
      </Stack>
    </div>
  );
};
